import React, { useEffect, useState } from 'react';
import * as Scrivito from 'scrivito';
import { findHomepage } from '../../lib/homepage';
import { INQUIRY_TAB_EVENT } from '../../lib/inquiryTab';
import { pick, resolveCmsField } from '../../lib/localize';

Scrivito.provideWidgetClass('InquiryWidget', {
  attributes: {
    inquiryText: 'html',
    inquiryButtonText: 'string',
    workForUsText: 'html',
    workForUsButtonText: 'string',
    sendButtonText: 'string',
    attachmentButtonTextOffer: 'string',
    attachmentButtonTextCareer: 'string',
    projectIntel: 'string',
    projectFurtherIntel: 'string',
    userDetails: 'string',
    requestType: 'string',
    expertise: 'string',
    firstName: 'string',
    lastName: 'string',
    email: 'string',
    subject: 'string',
    volume: 'string',
    sourceLanguage: 'string',
    targetLanguage: 'string',
    specialistAreas: 'string',
    remarksInquiry: 'string',
    remarksWorkForUs: 'string',
    dateLabel: 'string',
  },
});

// Every field here defaults to English on placement (see initialContent
// below) regardless of which language's homepage the widget lands on — nobody
// had gone back to translate them for the German page, so it showed English
// placeholders under a German nav. Rather than silently leaving that content
// gap in place, a field still holding its untouched English default is
// treated as "never localized" and swapped for its German equivalent here.
// The moment an editor types anything else into a field (including the
// German text below, verbatim), it stops matching `fallback` and this no
// longer applies — their explicit content always wins.
const DE_FALLBACKS = {
  inquiryButtonText: 'Projektanfrage',
  workForUsButtonText: 'Mitarbeit',
  sendButtonText: 'Senden',
  attachmentButtonTextOffer: 'Datei anhängen',
  attachmentButtonTextCareer: 'Lebenslauf / Portfolio anhängen',
  firstName: 'Vorname',
  lastName: 'Nachname',
  email: 'E-Mail-Adresse',
  subject: 'Betreff',
  volume: 'Umfang / Wortanzahl',
  sourceLanguage: 'Ausgangssprache',
  targetLanguage: 'Zielsprache',
  specialistAreas: 'Fachgebiete',
  remarksInquiry: 'Weitere Anmerkungen…',
  remarksWorkForUs: 'Erzählen Sie uns von sich…',
  dateLabel: 'Gewünschter Liefertermin',
};

function ph(widget, key, fallback, lang) {
  return resolveCmsField(widget.get(key), fallback, DE_FALLBACKS[key], lang).text;
}

// Same untouched-default check as ph(), but for the CMS-bound button/tab
// labels rendered through ContentTag: in the editor those must stay real,
// clickable, editable content; for everyone else, an untranslated default
// is swapped for German text instead.
function Localized({ widget, attribute, fallback, lang, editing, tag: Tag = 'span', className }) {
  const { text, localized } = resolveCmsField(widget.get(attribute), fallback, DE_FALLBACKS[attribute], lang);
  if (!editing && localized) {
    return <Tag className={className}>{text}</Tag>;
  }
  return <Scrivito.ContentTag tag={Tag} attribute={attribute} content={widget} className={className} />;
}

function HiddenFields({ loadedAt, objId }) {
  return (
    <>
      <div className="tt-hp-field" aria-hidden="true">
        <label htmlFor="tt_website">Website</label>
        <input id="tt_website" name="_hp" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      <input type="hidden" name="_loaded_at" value={loadedAt} />
      {/* Tells the server which homepage's mail wording to use. */}
      <input type="hidden" name="obj_id" value={objId} />
    </>
  );
}

const InquiryWidget = Scrivito.connect(({ widget }) => {
  // The homepage the form sits under decides the language of the confirmation
  // mail, whose wording editors maintain on that same obj, and doubles as the
  // signal for which language's fallback text the widget's own untranslated
  // fields should use (see DE_FALLBACKS above).
  const homepage = findHomepage(Scrivito.currentPage());
  const objId = homepage ? homepage.id() : '';
  const lang = (homepage?.get('isoCodeLanguage') || 'en').slice(0, 2).toLowerCase();
  const successText =
    homepage?.get('mailerSuccess') ||
    pick({ de: 'Nachricht gesendet — wir melden uns in Kürze.', en: 'Message sent — we will be in touch shortly.' }, lang);
  const errorText =
    pick({ de: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.', en: 'Something went wrong. Please try again.' }, lang);

  const [activeTab, setActiveTab] = useState('inquiry');
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fileLabel, setFileLabel] = useState(null);
  // Must be captured on mount, not read from a useState initializer: prerendered
  // pages (see api/prerender.mjs) are static HTML that can be served long after
  // it was generated, so a value baked in at render time would make the "at
  // least 3s between load and submit" anti-bot check in routes/inquiry.js
  // meaningless for real visitors. A useState initializer also runs during SSR,
  // so Date.now() there would differ from the client's on hydration and trip a
  // React hydration-mismatch error – this avoids that too.
  const [loadedAt, setLoadedAt] = useState(null);
  useEffect(() => {
    setLoadedAt(Date.now().toString());
  }, []);

  const handleSubmit = async (e, type) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await fetch('/api/inquiry', {
        method: 'POST',
        body: new FormData(e.target),
      });
      if (res.ok) {
        setStatus('success');
        e.target.reset();
        setFileLabel(null);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setStatus(null);
    setFileLabel(null);
  };

  // The hero and pricing/service buttons live in other widgets and preselect a
  // tab through a DOM event – see lib/inquiryTab.js.
  useEffect(() => {
    const onRequest = (e) => {
      if (e.detail === 'inquiry' || e.detail === 'work') handleTabSwitch(e.detail);
    };
    window.addEventListener(INQUIRY_TAB_EVENT, onRequest);
    return () => window.removeEventListener(INQUIRY_TAB_EVENT, onRequest);
  }, []);

  const isInquiry = activeTab === 'inquiry';
  const descAttr = isInquiry ? 'inquiryText' : 'workForUsText';
  const descText = widget.get(descAttr);
  // A freshly placed widget's intro text is blank by design (there's no
  // sensible default copy for it, unlike every other field here) — showing
  // its dark accent panel anyway rendered as an empty solid rectangle with
  // nothing in it. Editors still need it visible (and the form column at
  // its normal width) so they have somewhere to click and add the text.
  const editing = Scrivito.isInPlaceEditingActive();
  const showDesc = editing || Boolean(descText);

  return (
    <section id="request_information" className="tt-inquiry">
      <div className="container">

        {/* Tab bar */}
        <div className="tt-inquiry__tabs">
          <button
            className={`tt-inquiry__tab${isInquiry ? ' tt-inquiry__tab--active' : ''}`}
            onClick={() => handleTabSwitch('inquiry')}
          >
            <Localized widget={widget} attribute="inquiryButtonText" fallback="Project Inquiry" lang={lang} editing={editing} />
          </button>
          <button
            className={`tt-inquiry__tab${!isInquiry ? ' tt-inquiry__tab--active' : ''}`}
            onClick={() => handleTabSwitch('work')}
          >
            <Localized widget={widget} attribute="workForUsButtonText" fallback="Work With Us" lang={lang} editing={editing} />
          </button>
        </div>

        <div className="tt-inquiry__body row">

          {/* ── Form column (left) ── */}
          <div className={`${showDesc ? 'col-md-7' : 'col-md-12'} tt-inquiry__form-col`}>

            {status === 'success' && (
              <div className="tt-inquiry__alert tt-inquiry__alert--success">
                <i className="ti-check-box" /> {successText}
              </div>
            )}
            {status === 'error' && (
              <div className="tt-inquiry__alert tt-inquiry__alert--error">
                <i className="ti-alert" /> {errorText}
              </div>
            )}

            {isInquiry ? (
              <form
                key="inquiry"
                onSubmit={(e) => handleSubmit(e, 'project_inquiry')}
                encType="multipart/form-data"
              >
                <input type="hidden" name="type" value="project_inquiry" />
                <HiddenFields loadedAt={loadedAt} objId={objId} />

                <div className="row g-3">
                  <div className="col-sm-6">
                    <label htmlFor="inquiry-first-name" className="visually-hidden">
                      {ph(widget, 'firstName', 'First Name', lang)}
                    </label>
                    <input id="inquiry-first-name" name="first_name" className="tt-input" required
                      placeholder={ph(widget, 'firstName', 'First Name', lang)} />
                  </div>
                  <div className="col-sm-6">
                    <label htmlFor="inquiry-last-name" className="visually-hidden">
                      {ph(widget, 'lastName', 'Last Name', lang)}
                    </label>
                    <input id="inquiry-last-name" name="last_name" className="tt-input" required
                      placeholder={ph(widget, 'lastName', 'Last Name', lang)} />
                  </div>
                  <div className="col-12">
                    <label htmlFor="inquiry-email" className="visually-hidden">
                      {ph(widget, 'email', 'Email Address', lang)}
                    </label>
                    <input id="inquiry-email" name="email" type="email" className="tt-input" required
                      placeholder={ph(widget, 'email', 'Email Address', lang)} />
                  </div>
                  <div className="col-12">
                    <label htmlFor="inquiry-subject" className="visually-hidden">
                      {ph(widget, 'subject', 'Subject', lang)}
                    </label>
                    <input id="inquiry-subject" name="subject" className="tt-input"
                      placeholder={ph(widget, 'subject', 'Subject', lang)} />
                  </div>
                  <div className="col-sm-6">
                    <label htmlFor="inquiry-source-language" className="visually-hidden">
                      {ph(widget, 'sourceLanguage', 'Source Language', lang)}
                    </label>
                    <input id="inquiry-source-language" name="source_language" className="tt-input"
                      placeholder={ph(widget, 'sourceLanguage', 'Source Language', lang)} />
                  </div>
                  <div className="col-sm-6">
                    <label htmlFor="inquiry-target-language" className="visually-hidden">
                      {ph(widget, 'targetLanguage', 'Target Language', lang)}
                    </label>
                    <input id="inquiry-target-language" name="target_language" className="tt-input"
                      placeholder={ph(widget, 'targetLanguage', 'Target Language', lang)} />
                  </div>
                  <div className="col-sm-6">
                    <label htmlFor="inquiry-volume" className="visually-hidden">
                      {ph(widget, 'volume', 'Volume / Word Count', lang)}
                    </label>
                    <input id="inquiry-volume" name="volume" className="tt-input"
                      placeholder={ph(widget, 'volume', 'Volume / Word Count', lang)} />
                  </div>
                  {/* Native date inputs can't show custom placeholder text at
                      rest (only the browser's own date-format hint), so this
                      is the one field on the form with a real, visible label
                      instead of a visually-hidden one — otherwise nothing on
                      the page says what the date is for. */}
                  <div className="col-sm-6 tt-inquiry__date-field">
                    <label htmlFor="inquiry-date" className="tt-field-label">
                      {ph(widget, 'dateLabel', 'Desired delivery date', lang)}
                    </label>
                    <input id="inquiry-date" name="date" type="date" className="tt-input" />
                  </div>
                  <div className="col-12">
                    <label htmlFor="inquiry-remarks" className="visually-hidden">
                      {ph(widget, 'remarksInquiry', 'Additional remarks…', lang)}
                    </label>
                    <textarea id="inquiry-remarks" name="remarks" className="tt-input" rows={4}
                      placeholder={ph(widget, 'remarksInquiry', 'Additional remarks…', lang)} />
                  </div>
                  <div className="col-12 tt-inquiry__actions">
                    <label className="tt-file-btn">
                      <i className="ti-clip" />
                      {fileLabel || (
                        <Localized widget={widget} attribute="attachmentButtonTextOffer" fallback="Attach File" lang={lang} editing={editing} />
                      )}
                      <input
                        name="attachment_offer" type="file" className="d-none"
                        accept=".jpg,.jpeg,.pdf,.zip,.docx"
                        onChange={(e) => setFileLabel(e.target.files[0]?.name || null)}
                      />
                    </label>
                    <button type="submit" className="tt-submit-btn" disabled={submitting}>
                      {submitting
                        ? <span className="tt-spinner" />
                        : <i className="ti-arrow-right" />
                      }
                      <Localized widget={widget} attribute="sendButtonText" fallback="Send" lang={lang} editing={editing} />
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <form
                key="work"
                onSubmit={(e) => handleSubmit(e, 'work_with_usw')}
                encType="multipart/form-data"
              >
                <input type="hidden" name="type" value="work_with_usw" />
                <HiddenFields loadedAt={loadedAt} objId={objId} />

                <div className="row g-3">
                  <div className="col-sm-6">
                    <label htmlFor="work-first-name" className="visually-hidden">
                      {ph(widget, 'firstName', 'First Name', lang)}
                    </label>
                    <input id="work-first-name" name="first_name" className="tt-input" required
                      placeholder={ph(widget, 'firstName', 'First Name', lang)} />
                  </div>
                  <div className="col-sm-6">
                    <label htmlFor="work-last-name" className="visually-hidden">
                      {ph(widget, 'lastName', 'Last Name', lang)}
                    </label>
                    <input id="work-last-name" name="last_name" className="tt-input" required
                      placeholder={ph(widget, 'lastName', 'Last Name', lang)} />
                  </div>
                  <div className="col-12">
                    <label htmlFor="work-email" className="visually-hidden">
                      {ph(widget, 'email', 'Email Address', lang)}
                    </label>
                    <input id="work-email" name="email" type="email" className="tt-input" required
                      placeholder={ph(widget, 'email', 'Email Address', lang)} />
                  </div>
                  <div className="col-12">
                    <label htmlFor="work-specialist-areas" className="visually-hidden">
                      {ph(widget, 'specialistAreas', 'Specialist Areas', lang)}
                    </label>
                    <input id="work-specialist-areas" name="specialist_areas" className="tt-input"
                      placeholder={ph(widget, 'specialistAreas', 'Specialist Areas', lang)} />
                  </div>
                  <div className="col-sm-6">
                    <label htmlFor="work-source-language" className="visually-hidden">
                      {ph(widget, 'sourceLanguage', 'Source Language', lang)}
                    </label>
                    <input id="work-source-language" name="source_language" className="tt-input"
                      placeholder={ph(widget, 'sourceLanguage', 'Source Language', lang)} />
                  </div>
                  <div className="col-sm-6">
                    <label htmlFor="work-target-language" className="visually-hidden">
                      {ph(widget, 'targetLanguage', 'Target Language', lang)}
                    </label>
                    <input id="work-target-language" name="target_language" className="tt-input"
                      placeholder={ph(widget, 'targetLanguage', 'Target Language', lang)} />
                  </div>
                  <div className="col-12">
                    <label htmlFor="work-remarks" className="visually-hidden">
                      {ph(widget, 'remarksWorkForUs', 'Tell us about yourself…', lang)}
                    </label>
                    <textarea id="work-remarks" name="remarks" className="tt-input" rows={4}
                      placeholder={ph(widget, 'remarksWorkForUs', 'Tell us about yourself…', lang)} />
                  </div>
                  <div className="col-12 tt-inquiry__actions">
                    <label className="tt-file-btn">
                      <i className="ti-clip" />
                      {fileLabel || (
                        <Localized widget={widget} attribute="attachmentButtonTextCareer" fallback="Attach CV / Portfolio" lang={lang} editing={editing} />
                      )}
                      <input
                        name="attachment_career" type="file" className="d-none"
                        accept=".jpg,.jpeg,.pdf,.zip,.docx"
                        onChange={(e) => setFileLabel(e.target.files[0]?.name || null)}
                      />
                    </label>
                    <button type="submit" className="tt-submit-btn" disabled={submitting}>
                      {submitting
                        ? <span className="tt-spinner" />
                        : <i className="ti-arrow-right" />
                      }
                      <Localized widget={widget} attribute="sendButtonText" fallback="Send" lang={lang} editing={editing} />
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* ── Description column (right) ── */}
          {showDesc && (
            <div className="col-md-5 tt-inquiry__desc-col">
              <div className="tt-inquiry__desc">
                <Scrivito.ContentTag tag="div" attribute={descAttr} content={widget} />
              </div>
            </div>
          )}

        </div>
      </div>
    </section>
  );
});

Scrivito.provideComponent('InquiryWidget', InquiryWidget);

Scrivito.provideEditingConfig('InquiryWidget', {
  title: 'Inquiry Form',
  propertiesGroups: [
    {
      title: 'Tab Labels & Texts',
      properties: ['inquiryButtonText', 'inquiryText', 'workForUsButtonText', 'workForUsText', 'sendButtonText', 'attachmentButtonTextOffer', 'attachmentButtonTextCareer'],
    },
    {
      title: 'Form Placeholders',
      properties: ['firstName', 'lastName', 'email', 'subject', 'volume', 'sourceLanguage', 'targetLanguage', 'specialistAreas', 'dateLabel', 'remarksInquiry', 'remarksWorkForUs'],
    },
  ],
  attributes: {
    // Tab labels & texts
    inquiryButtonText:          { title: 'Tab: Project Inquiry Label' },
    inquiryText:                { title: 'Project Inquiry – Intro Text' },
    workForUsButtonText:        { title: 'Tab: Work With Us Label' },
    workForUsText:              { title: 'Work With Us – Intro Text' },
    sendButtonText:             { title: 'Submit Button Text' },
    attachmentButtonTextOffer:  { title: 'Attachment Button (Inquiry)' },
    attachmentButtonTextCareer: { title: 'Attachment Button (Career)' },
    // Form placeholders
    firstName:      { title: 'Placeholder: First Name' },
    lastName:       { title: 'Placeholder: Last Name' },
    email:          { title: 'Placeholder: Email Address' },
    subject:        { title: 'Placeholder: Subject' },
    volume:         { title: 'Placeholder: Volume / Word Count' },
    sourceLanguage: { title: 'Placeholder: Source Language' },
    targetLanguage: { title: 'Placeholder: Target Language' },
    specialistAreas:  { title: 'Placeholder: Specialist Areas' },
    dateLabel:        { title: 'Label: Desired Delivery Date', description: 'Shown above the date picker (Project Inquiry tab only) — native date inputs can\'t show a placeholder, so without a real label nothing explains what the date is for.' },
    remarksInquiry:   { title: 'Placeholder: Remarks (Inquiry)' },
    remarksWorkForUs: { title: 'Placeholder: Remarks (Work With Us)' },
  },
  initialContent: {
    inquiryButtonText:          'Project Inquiry',
    workForUsButtonText:        'Work With Us',
    sendButtonText:             'Send',
    attachmentButtonTextOffer:  'Attach File',
    attachmentButtonTextCareer: 'Attach CV / Portfolio',
    firstName:      'First Name',
    lastName:       'Last Name',
    email:          'Email Address',
    subject:        'Subject',
    volume:         'Volume / Word Count',
    sourceLanguage: 'Source Language',
    targetLanguage: 'Target Language',
    specialistAreas:  'Specialist Areas',
    dateLabel:        'Desired delivery date',
    remarksInquiry:   'Additional remarks…',
    remarksWorkForUs: 'Tell us about yourself…',
  },
});

export default InquiryWidget;
